import type { SlotType } from "@/types/entities/templates";
import { Mark, mergeAttributes } from "@tiptap/core";
import type { Node } from "@tiptap/pm/model";
import { Plugin, PluginKey, Selection, TextSelection } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

export const SlotMark = Mark.create({
  name: "slot",

  addOptions() {
    return {
      slotPattern: /\{\{([a-z0-9_-]+)(?::([a-z]+))?\}\}\s/gi,
    };
  },

  parseHTML() {
    return [
      {
        tag: "span[data-slot]",
      },
    ];
  },

  renderHTML({ HTMLAttributes }: { HTMLAttributes: Record<string, string> }) {
    const slotName = HTMLAttributes["data-slot-name"] || "";
    const slotType = HTMLAttributes["data-slot-type"] || "string";

    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        "data-slot": "",
        "data-slot-name": slotName,
        "data-slot-type": slotType,
        class: "slot-badge",
      }),
      0,
    ];
  },

  addProseMirrorPlugins() {
    const getDecorations = (doc: Node, selection: Selection) => {
      const decorations: Decoration[] = [];

      doc.descendants((node, pos, parent) => {
        if (!node.isText) return;

        const text = node.text || "";

        const inProgressPattern = /\{\{([^}]*)\}\}/g;
        let inProgressMatch;

        while ((inProgressMatch = inProgressPattern.exec(text)) !== null) {
          const from = pos + inProgressMatch.index;
          const to = from + inProgressMatch[0].length;
          const content = inProgressMatch[1];

          const nextChar =
            text[inProgressMatch.index + inProgressMatch[0].length];
          if (nextChar !== " " && nextChar !== undefined) {
            decorations.push(
              Decoration.inline(from, to, {
                class: "slot-in-progress",
              }),
            );

            if (content.includes(":")) {
              const colonIndex = content.indexOf(":");
              const typeText = content.substring(colonIndex + 1).toLowerCase();
              const typeStart =
                pos + inProgressMatch.index + 2 + colonIndex + 1;
              const typeEnd = to - 2;

              const isValidType = ["string", "number", "date"].includes(
                typeText,
              );

              decorations.push(
                Decoration.inline(typeStart, typeEnd, {
                  class: isValidType
                    ? "slot-type-indicator"
                    : "slot-type-invalid",
                }),
              );
            }
            continue;
          }

          if (content.includes(":")) {
            const colonIndex = content.indexOf(":");
            const typeStart = pos + inProgressMatch.index + 2 + colonIndex + 1;
            const typeEnd = to - 2;

            decorations.push(
              Decoration.inline(typeStart, typeEnd, {
                class: "slot-type-indicator",
              }),
            );
          }
        }

        const finalizedPattern = /\{\{([a-z0-9_-]+)(?::([a-z]+))?\}\}/gi;
        finalizedPattern.lastIndex = 0;
        let match;

        while ((match = finalizedPattern.exec(text)) !== null) {
          const from = pos + match.index;
          const to = from + match[0].length;
          const slotName = match[1];
          const slotType = match[2] || "string";

          const nextChar = text[match.index + match[0].length];

          const hasSpace = nextChar === " ";
          const isSelected =
            !selection.empty && selection.from <= from && selection.to >= to;

          if (!hasSpace && !isSelected) {
            continue;
          }

          const marks = node.marks || [];
          const markClasses = marks
            .map((mark) => {
              switch (mark.type.name) {
                case "bold":
                  return "font-bold";
                case "italic":
                  return "italic";
                case "underline":
                  return "underline";
                case "strike":
                  return "line-through";
                default:
                  return "";
              }
            })
            .filter(Boolean)
            .join(" ");

          let nodeClass = "";
          if (parent && parent.type.name === "heading") {
            const level = parent.attrs.level || 1;
            nodeClass = `heading-${level}`;
          }

          const widget = document.createElement("span");
          widget.className =
            `${isSelected ? "slot-badge slot-selected" : "slot-badge"} ${markClasses}${nodeClass}`.trim();

          widget.setAttribute("data-slot-name", slotName);
          widget.setAttribute("data-slot-type", slotType);
          widget.contentEditable = "false";
          //widget.style.pointerEvents = "auto";

          const nameSpan = document.createElement("span");
          nameSpan.className = "slot-name";
          nameSpan.textContent = slotName;

          const iconSpan = document.createElement("span");
          iconSpan.className = "slot-icon";

          const iconElement = document.createElement("i");
          const iconClass =
            slotType === "number"
              ? "fa-hashtag"
              : slotType === "date"
                ? "fa-calendar"
                : "fa-text";

          iconElement.className = `far ${iconClass}`;
          iconSpan.appendChild(iconElement);

          widget.appendChild(nameSpan);
          widget.appendChild(iconSpan);

          decorations.push(
            Decoration.widget(from, widget, {
              side: -1,
              key: `slot-${slotName}-${from}-${isSelected ? "selected" : "normal"}`,
            }),
          );

          const hideEnd = hasSpace ? to + 1 : to;
          decorations.push(
            Decoration.inline(from, hideEnd, {
              nodeName: "span",
              style:
                "font-size: 0; width: 0; height: 0; display: inline; opacity: 0; position: absolute;",
            }),
          );
        }
      });

      return DecorationSet.create(doc, decorations);
    };

    return [
      new Plugin({
        key: new PluginKey("slotAutoClose"),
        props: {
          handleTextInput(view, from, to, text) {
            if (text === "{") {
              const $pos = view.state.doc.resolve(from);
              const before = $pos.parent.textBetween(
                Math.max(0, $pos.parentOffset - 1),
                $pos.parentOffset,
                null,
                "\ufffc",
              );

              if (before === "{") {
                const tr = view.state.tr;
                tr.insertText("{", from, to);
                tr.insertText("}}", from + 1);
                tr.setSelection(TextSelection.create(tr.doc, from + 1));
                view.dispatch(tr);
                return true;
              }
            }
            return false;
          },
        },
      }),
      new Plugin({
        key: new PluginKey("slotHighlight"),

        state: {
          init(_, state) {
            return getDecorations(state.doc, state.selection);
          },
          apply(tr, decorations, _oldState, newState) {
            if (tr.docChanged) {
              return getDecorations(newState.doc, newState.selection);
            }
            return decorations.map(tr.mapping, tr.doc);
          },
        },

        props: {
          decorations(state) {
            return this.getState(state);
          },
        },
      }),
      new Plugin({
        key: new PluginKey("slotActivate"),
        props: {
          handleKeyDown(view, event) {
            if (event.key === "Enter") {
              const { from } = view.state.selection;
              const $pos = view.state.doc.resolve(from);

              const fullText = $pos.parent.textContent;
              const cursorPos = $pos.parentOffset;

              const beforeCursor = fullText.substring(0, cursorPos);
              const afterCursor = fullText.substring(cursorPos);

              const hasOpenBrackets = /\{\{([a-z0-9_-]+)(?::([a-z]+))?$/i.test(
                beforeCursor,
              );

              const hasCloseBrackets = /^\}\}/i.test(afterCursor);

              if (hasOpenBrackets && hasCloseBrackets) {
                event.preventDefault();
                event.stopPropagation();

                const match = /\{\{([a-z0-9_-]+)(?::([a-z]+))?$/i.exec(
                  beforeCursor,
                );

                if (match) {
                  const slotName = match[1];
                  const slotType = match[2] ? match[2].toLowerCase() : null;

                  const validTypes = ["string", "number", "date"];
                  const isValidType =
                    !slotType || validTypes.includes(slotType);

                  if (slotType && !isValidType) {
                    const tr = view.state.tr;
                    const slotStart = from - match[0].length;
                    const slotEnd = from + 2;

                    const correctedSlot = `{{${slotName}}} `;
                    tr.replaceWith(
                      slotStart,
                      slotEnd,
                      view.state.schema.text(correctedSlot),
                    );

                    const newPos = slotStart + correctedSlot.length;
                    tr.setSelection(TextSelection.create(tr.doc, newPos));

                    view.dispatch(tr);

                    setTimeout(() => {
                      const tr2 = view.state.tr;
                      tr2.insertText(" ", view.state.selection.from);
                      tr2.setSelection(
                        TextSelection.create(
                          tr2.doc,
                          view.state.selection.from + 1,
                        ),
                      );
                      view.dispatch(tr2);
                      view.focus();
                    }, 0);
                    return true;
                  }
                }

                const tr = view.state.tr;
                const newPos = from + 2;
                tr.setSelection(TextSelection.create(tr.doc, newPos));
                tr.insertText(" ", newPos);
                view.dispatch(tr);

                setTimeout(() => {
                  const tr2 = view.state.tr;
                  tr2.insertText(" ", view.state.selection.from);
                  tr2.setSelection(
                    TextSelection.create(
                      tr2.doc,
                      view.state.selection.from + 1,
                    ),
                  );
                  view.dispatch(tr2);
                  view.focus();
                }, 0);

                return true;
              }

              const matchAfter = /\{\{([a-z0-9_-]+)(?::([a-z]+))?\}\}$/i.exec(
                beforeCursor,
              );
              if (matchAfter && !hasCloseBrackets) {
                event.preventDefault();
                event.stopPropagation();

                const slotType = matchAfter[2]
                  ? matchAfter[2].toLowerCase()
                  : null;
                const validTypes = ["string", "number", "date"];

                const isValidType = !slotType || validTypes.includes(slotType);

                //if (slotType && !validTypes.includes(slotType)) {
                if (slotType && !isValidType) {
                  const slotName = matchAfter[1];
                  const slotStart = from - matchAfter[0].length;

                  const tr = view.state.tr;
                  const correctedSlot = `{{${slotName}}} `;
                  tr.replaceWith(
                    slotStart,
                    from,
                    view.state.schema.text(correctedSlot),
                  );

                  const newPos = slotStart + correctedSlot.length;
                  tr.setSelection(TextSelection.create(tr.doc, newPos));
                  view.dispatch(tr);
                  setTimeout(() => {
                    const tr2 = view.state.tr;
                    tr2.insertText(" ", view.state.selection.from);
                    tr2.setSelection(
                      TextSelection.create(
                        tr2.doc,
                        view.state.selection.from + 1,
                      ),
                    );
                    view.dispatch(tr2);
                    view.focus();
                  }, 0);
                  return true;
                }

                const tr = view.state.tr;
                tr.insertText(" ", from);
                view.dispatch(tr);

                setTimeout(() => {
                  const tr2 = view.state.tr;
                  tr2.insertText(" ", view.state.selection.from);
                  tr2.setSelection(
                    TextSelection.create(
                      tr2.doc,
                      view.state.selection.from + 1,
                    ),
                  );
                  view.dispatch(tr2);
                  view.focus();
                }, 0);

                return true;
              }
            }
            return false;
          },
        },
      }),
      new Plugin({
        key: new PluginKey("slotSpaceReplace"),
        props: {
          handleTextInput(view, from, to, text) {
            if (text === " ") {
              const { from: selFrom } = view.state.selection;
              const $pos = view.state.doc.resolve(selFrom);

              const fullText = $pos.parent.textContent;
              const cursorPos = $pos.parentOffset;
              const beforeCursor = fullText.substring(0, cursorPos);
              const afterCursor = fullText.substring(cursorPos);

              const hasOpenBrackets = /\{\{[^}]*$/i.test(beforeCursor);
              const hasCloseBrackets = /^\}\}/i.test(afterCursor);

              if (hasOpenBrackets && hasCloseBrackets) {
                const tr = view.state.tr;
                tr.insertText("_", from, to);
                view.dispatch(tr);
                return true;
              }
            }
            return false;
          },
        },
      }),
      new Plugin<{ from: number; to: number } | null>({
        key: new PluginKey("slotSelection"),

        state: {
          init(): { from: number; to: number } | null {
            return null;
          },
          apply(tr, value): { from: number; to: number } | null {
            if (tr.selectionSet || tr.docChanged) {
              const { from, to, empty } = tr.selection;

              if (!empty) {
                const selectedText = tr.doc.textBetween(from, to);
                if (/^\{\{[a-z0-9_-]+(?::[a-z]+)?\}\}$/i.test(selectedText)) {
                  return { from, to };
                }
              }
              return null;
            }
            return value;
          },
        },

        props: {
          handleClick(view, pos, event) {
            const target = event.target as HTMLElement;
            const slotBadge = target.closest(".slot-badge");

            if (slotBadge) {
              event.preventDefault();

              const $pos = view.state.doc.resolve(pos);
              const textContent = $pos.parent.textContent;

              const slotName = slotBadge.getAttribute("data-slot-name");
              const slotType =
                slotBadge.getAttribute("data-slot-type") || "string";

              const slotText =
                slotType === "string"
                  ? `{{${slotName}}}`
                  : `{{${slotName}:${slotType}}}`;

              let bestMatch = { from: -1, to: -1, distance: Infinity };
              let currentIndex = 0;

              while (
                (currentIndex = textContent.indexOf(slotText, currentIndex)) !==
                -1
              ) {
                const absoluteFrom = $pos.start() + currentIndex;
                const absoluteTo = absoluteFrom + slotText.length;
                const distance = Math.abs(pos - absoluteFrom);

                if (distance < bestMatch.distance) {
                  bestMatch = { from: absoluteFrom, to: absoluteTo, distance };
                }

                currentIndex += slotText.length;
              }

              if (bestMatch.from !== -1) {
                document
                  .querySelectorAll(".slot-badge.slot-selected")
                  .forEach((el) => {
                    el.classList.remove("slot-selected");
                  });

                (slotBadge as HTMLElement).classList.add("slot-selected");

                const tr = view.state.tr;
                tr.setSelection(
                  TextSelection.create(tr.doc, bestMatch.from, bestMatch.to),
                );
                view.dispatch(tr);

                setTimeout(() => {
                  (slotBadge as HTMLElement).classList.add("slot-selected");
                }, 0);

                return true;
              }
            }
            return false;
          },

          handleDoubleClick(view, pos, event) {
            const target = event.target as HTMLElement;
            const slotBadge = target.closest(".slot-badge");

            if (slotBadge) {
              event.preventDefault();

              const slotName = slotBadge.getAttribute("data-slot-name");
              const slotType =
                (slotBadge.getAttribute("data-slot-type") as SlotType) ||
                "string";

              const $pos = view.state.doc.resolve(pos);
              const textContent = $pos.parent.textContent;

              let slotText = `{{${slotName}:${slotType}}} `;
              let foundIndex = textContent.indexOf(slotText);

              if (foundIndex === -1) {
                slotText = `{{${slotName}}} `;
                foundIndex = textContent.indexOf(slotText);
              }

              if (foundIndex === -1) {
                console.error("❌ Could not find clicked slot", {
                  slotName,
                  slotType,
                  textContent,
                });
                return false;
              }

              let currentIndex = 0;
              while (
                (currentIndex = textContent.indexOf(slotText, currentIndex)) !==
                -1
              ) {
                const absoluteFrom = $pos.start() + currentIndex;
                const absoluteTo = absoluteFrom + slotText.length - 1;

                if (pos >= absoluteFrom && pos <= absoluteTo) {
                  foundIndex = currentIndex;
                  break;
                }

                currentIndex += slotText.length;
              }

              const from = $pos.start() + foundIndex;
              const to = from + slotText.length - 1;

              const customEvent = new CustomEvent("openSlotModal", {
                detail: { slotName, slotType, mode: "edit", from, to },
              });
              document.dispatchEvent(customEvent);

              return true;
            }
            return false;
          },

          handleKeyDown(view, event) {
            if (event.key === " " || event.code === "Space") {
              const { from, to, empty } = view.state.selection;

              if (!empty) {
                const selectedText = view.state.doc.textBetween(from, to);

                if (/^\{\{[a-z0-9_-]+(?::[a-z]+)?\}\}$/i.test(selectedText)) {
                  event.preventDefault();

                  const tr = view.state.tr;

                  tr.insertText(" ", to);

                  tr.setSelection(TextSelection.create(tr.doc, to + 1));

                  view.dispatch(tr);

                  setTimeout(() => {
                    const tr2 = view.state.tr;
                    tr2.insertText(" ", view.state.selection.from);
                    tr2.setSelection(
                      TextSelection.create(
                        tr2.doc,
                        view.state.selection.from + 1,
                      ),
                    );
                    view.dispatch(tr2);
                    view.focus();
                  }, 0);

                  return true;
                }
              }
            }

            if (event.key === "Backspace") {
              const { from, to, empty } = view.state.selection;

              if (!empty) {
                const selectedText = view.state.doc.textBetween(from, to);

                if (/^\{\{[a-z0-9_-]+(?::[a-z]+)?\}\}$/i.test(selectedText)) {
                  event.preventDefault();

                  const tr = view.state.tr;
                  tr.delete(from, to);
                  view.dispatch(tr);

                  return true;
                }
              }

              if (empty && from > 1) {
                const $pos = view.state.doc.resolve(from);
                const textBefore = $pos.parent.textBetween(
                  Math.max(0, $pos.parentOffset - 20),
                  $pos.parentOffset,
                );

                const slotMatch = /(\{\{[a-z0-9_-]+(?::[a-z]+)?\}\})\s$/i.exec(
                  textBefore,
                );

                if (slotMatch) {
                  event.preventDefault();

                  const tr = view.state.tr;
                  const spacePos = from - 1;
                  const slotStart = from - slotMatch[0].length;
                  const slotEnd = from - 1;

                  tr.delete(spacePos, from);
                  tr.setSelection(
                    TextSelection.create(tr.doc, slotStart, slotEnd),
                  );
                  view.dispatch(tr);

                  return true;
                }
              }
            }
            return false;
          },
        },
      }),
    ];
  },
});
